<template>
  <div>
    <user-list-add-new
      :is-add-new-user-sidebar-active.sync="isAddNewUserSidebarActive"
      :role-options="roleOptions"
      @refetch-data="refetchData"
    />

    <b-card no-body>
      <b-card-header class="px-2 pt-1">
        <h5>Filters</h5>
      </b-card-header>
      <b-card-body class="px-2 py-1">
        <b-row>
          <b-col
            cols="12"
            md="4"
            class="mb-2 mb-md-0"
          >
            <label>Role</label>
            <b-form-select
              v-model="roleFilter"
              :options="roles"
            />
          </b-col>
          <b-col
            cols="12"
            md="4"
            class="mb-2 mb-md-0"
          >
            <label>Branch</label>
            <v-select
              v-model="branchFilter"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="branches"
              :reduce="branch => branch.value"
              placeholder="All branches"
              class="w-100"
            />
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>entries</label>

            <div
              class="view-switch ml-2"
              role="group"
              aria-label="Switch view"
            >
              <button
                type="button"
                class="view-switch__btn"
                :class="{ 'view-switch__btn--active': viewMode === 'card' }"
                @click="viewMode = 'card'"
              >
                <feather-icon
                  icon="GridIcon"
                  size="14"
                />
                <span>Cards</span>
              </button>
              <button
                type="button"
                class="view-switch__btn"
                :class="{ 'view-switch__btn--active': viewMode === 'table' }"
                @click="viewMode = 'table'"
              >
                <feather-icon
                  icon="ListIcon"
                  size="14"
                />
                <span>Table</span>
              </button>
            </div>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                placeholder="Search..."
              />
              <b-button
                variant="primary"
                @click="isAddNewUserSidebarActive = true"
              >
                <span class="text-nowrap">Add User</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        v-if="viewMode === 'table'"
        ref="refUserListTable"
        class="position-relative"
        :items="fetchUsers"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
        :busy="loading"
      >
        <template #table-busy>
          <shimmer-table-rows
            :rows="Math.min(perPage, 8)"
            :columns="tableColumns.length - 1"
          />
        </template>

        <template #cell(user_fullname)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                size="32"
                :src="data.item.avatar"
                :text="avatarText(data.item.user_fullname)"
              />
            </template>
              {{ data.item.user_fullname ? data.item.user_fullname.charAt(0).toUpperCase() + data.item.user_fullname.slice(1) : '' }}
          </b-media>
        </template>

        <template #cell(user_username)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.user_username }}</span>
          </div>
        </template>

        <template #cell(user_phone)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.user_phone }}</span>
          </div>
        </template>

        <template #cell(user_role)="data">
          <div class="text-nowrap">
            <feather-icon
              :icon="resolveUserRoleIcon(data.item.user_role)"
              size="18"
              class="mr-50"
            />
            <span class="align-text-top text-capitalize">{{ data.item.user_role_name ? data.item.user_role_name : '-' }}</span>
          </div>
        </template>

        <template #cell(user_branch_code)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.branch ? data.item.branch.branch_code : '-' }}</span>
          </div>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item @click="editUser(data.item)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item @click="deleteUser(data.item.user_id)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>

      </b-table>

      <!-- Card view -->
      <div
        v-if="viewMode === 'card'"
        class="user-cards"
      >
        <div
          v-if="isLoadingUsers"
          class="user-cards__loading"
        >
          <shimmer-card-grid :count="perPage" />
        </div>

        <div
          v-else-if="!users.length"
          class="user-cards__empty"
        >
          <feather-icon
            icon="UsersIcon"
            size="30"
          />
          <strong>No matching records found</strong>
        </div>

        <div
          v-else
          class="user-cards__body"
        >
          <div class="user-cards__rail d-none d-md-flex">
            <button
              v-for="letter in alphabet"
              :key="letter"
              type="button"
              class="user-cards__rail-letter"
              :class="{ 'user-cards__rail-letter--active': availableLetters.has(letter) }"
              :disabled="!availableLetters.has(letter)"
              @click="scrollToLetter(letter)"
            >
              {{ letter }}
            </button>
          </div>

          <div
            ref="cardScrollArea"
            class="user-cards__scroll"
          >
            <div
              v-for="group in groupedUsers"
              :id="`user-letter-${group.letter}`"
              :key="group.letter"
              class="user-cards__group"
            >
              <div class="user-cards__group-heading">
                <span>{{ group.letter }}</span>
                <em>{{ group.users.length }} {{ group.users.length === 1 ? 'person' : 'people' }}</em>
              </div>

              <div class="user-cards__grid">
                <div
                  v-for="user in group.users"
                  :key="user.user_id"
                  class="user-card"
                >
                  <div class="user-card__top">
                    <b-avatar
                      size="46"
                      :src="user.avatar"
                      :text="avatarText(user.user_fullname)"
                      :variant="`light-${resolveUserRoleVariant(user.user_role)}`"
                      class="user-card__avatar"
                    />
                    <b-dropdown
                      variant="link"
                      no-caret
                      right
                      class="user-card__menu"
                    >
                      <template #button-content>
                        <feather-icon
                          icon="MoreVerticalIcon"
                          size="16"
                          class="align-middle text-body"
                        />
                      </template>
                      <b-dropdown-item @click="editUser(user)">
                        <feather-icon icon="EditIcon" />
                        <span class="align-middle ml-50">Edit</span>
                      </b-dropdown-item>
                      <b-dropdown-item @click="deleteUser(user.user_id)">
                        <feather-icon icon="TrashIcon" />
                        <span class="align-middle ml-50">Delete</span>
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>

                  <h6 class="user-card__name">
                    {{ user.user_fullname ? user.user_fullname.charAt(0).toUpperCase() + user.user_fullname.slice(1) : '-' }}
                  </h6>

                  <b-badge
                    pill
                    :variant="`light-${resolveUserRoleVariant(user.user_role)}`"
                    class="user-card__role"
                  >
                    <feather-icon
                      :icon="resolveUserRoleIcon(user.user_role)"
                      size="12"
                      class="mr-25"
                    />
                    {{ user.user_role_name || '-' }}
                  </b-badge>

                  <ul class="user-card__meta">
                    <li>
                      <feather-icon
                        icon="AtSignIcon"
                        size="13"
                      />
                      <span>{{ user.user_username || '-' }}</span>
                    </li>
                    <li v-if="user.user_phone">
                      <feather-icon
                        icon="PhoneIcon"
                        size="13"
                      />
                      <span>{{ user.user_phone }}</span>
                    </li>
                    <li>
                      <feather-icon
                        icon="MapPinIcon"
                        size="13"
                      />
                      <span>{{ user.branch ? user.branch.branch_code : (user.user_branch_code || '-') }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
          </b-col>

          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalUsers || (currentPage * perPage)"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar,
  BBadge, BDropdown, BDropdownItem, BPagination, BFormSelect, BSpinner,
} from 'bootstrap-vue'
import axios from 'axios'
import vSelect from 'vue-select'
import store from '@/store'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ShimmerCardGrid from '@core/components/skeleton/ShimmerCardGrid.vue'
import ShimmerTableRows from '@core/components/skeleton/ShimmerTableRows.vue'
import { ref, onUnmounted } from '@vue/composition-api'
import { avatarText } from '@core/utils/filter'
import apiEndpoints from '@/constants/apiEndpoints'
import listPagePersistence from '@/mixins/listPagePersistence'
import useUsersList from './useUsersList'
import userStoreModule from '../userStoreModule'
import UserListAddNew from './UserListAddNew.vue'

export default {
  components: {
    UserListAddNew,
    ShimmerCardGrid,
    ShimmerTableRows,
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BFormSelect,
    BSpinner,
    vSelect,
  },
  mixins: [listPagePersistence(['currentPage'])],
  data() {
    return {
      roles: [],
      branches: [],
      alphabet: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''),
    }
  },
  computed: {
    groupedUsers() {
      const groups = {}
      this.users.forEach(user => {
        const initial = (user.user_fullname || '').trim().charAt(0).toUpperCase()
        const letter = /[A-Z]/.test(initial) ? initial : '#'
        if (!groups[letter]) groups[letter] = []
        groups[letter].push(user)
      })
      return Object.keys(groups)
        .sort()
        .map(letter => ({ letter, users: groups[letter] }))
    },
    availableLetters() {
      return new Set(this.groupedUsers.map(group => group.letter))
    },
  },
  mounted() {
    this.getRoles()
    this.getBranches()
    if (this.viewMode === 'card') {
      this.fetchUsersData()
    }
    this.$root.$on('refreshTable', () => {
      this.refetchData()
      this.isAddNewUserSidebarActive = false
    })
  },
  setup() {
    const USER_APP_STORE_MODULE_NAME = 'app-user'

    // Register module
    if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) store.registerModule(USER_APP_STORE_MODULE_NAME, userStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(USER_APP_STORE_MODULE_NAME)) store.unregisterModule(USER_APP_STORE_MODULE_NAME)
    })

    const isAddNewUserSidebarActive = ref(false)

    const statusOptions = [
      { label: 'Pending', value: 'pending' },
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ]

    const {
      fetchUsers,
      fetchUsersData,
      fetchRoles,
      tableColumns,
      perPage,
      currentPage,
      totalUsers,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,

      // Switchable views
      viewMode,
      users,
      isLoadingUsers,
      loading,

      // UI
      resolveUserRoleVariant,
      resolveUserRoleIcon,
      resolveUserStatusVariant,

      // Extra Filters
      roleFilter,
      branchFilter,
      statusFilter,
    } = useUsersList()

    const roleOptions = [
      { label: 'Admin', value: 'admin' },
      { label: 'Author', value: 'author' },
      { label: 'Editor', value: 'editor' },
      { label: 'Maintainer', value: 'maintainer' },
      { label: 'Subscriber', value: 'subscriber' },
    ]

    return {
      // Sidebar
      isAddNewUserSidebarActive,

      fetchUsers,
      fetchUsersData,
      fetchRoles,
      tableColumns,
      perPage,
      currentPage,
      totalUsers,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,

      // Switchable views
      viewMode,
      users,
      isLoadingUsers,
      loading,

      // Filter
      avatarText,

      // UI
      resolveUserRoleVariant,
      resolveUserRoleIcon,
      resolveUserStatusVariant,

      roleOptions,
      statusOptions,

      // Extra Filters
      roleFilter,
      branchFilter,
      statusFilter,
    }
  },
  methods: {
    getBranches() {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }
      axios
        .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_BRANCHES}?all=true`, { headers })
        .then(response => {
          if (response.data && response.data.data) {
            this.branches = response.data.data
              .map(elem => ({
                label: `(${elem.branch_code}) ${elem.branch_name || elem.branch_address || '-'}`,
                value: elem.branch_id,
                code: elem.branch_code,
              }))
              .sort((a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true }))
          }
        })
        .catch(err => {
          console.error(err)
        })
    },
    getRoles() {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }
      axios
        .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_ROLES}`, { headers })
        .then(response => {
          if (response.data && response.data.data) {
            const rolesJoin = response.data.data.map(elem => (
              { text: elem.role_name, value: elem.role_id }
            ))
            this.roles = [{ text: 'All roles', value: null }, ...rolesJoin]
          }
        })
        .catch(err => {
          console.error(err)
        })
    },
    deleteUser(id) {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }
      const url = `${process.env.VUE_APP_API_URL}${apiEndpoints.API_DELETE_USER}/${id}`
      axios
        .get(url, { headers })
        .then(response => {
          if (response.data.success === true) {
            this.refetchData()
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Success',
                icon: 'CheckIcon',
                variant: 'success',
                text: 'Your data has been deleted!',
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Notification',
                icon: 'BellIcon',
                variant: 'danger',
                text: response.data.message || 'Something went wrong',
              },
            })
          }
        })
        .catch(err => {
          console.error(err)
        })
    },
    editUser(data) {
      this.$cookies.set('userEdit', data)
      this.$router.push({ name: 'apps-users-edit' })
    },
    scrollToLetter(letter) {
      const el = document.getElementById(`user-letter-${letter}`)
      const container = this.$refs.cardScrollArea
      if (!el) return
      if (container) {
        const top = el.offsetTop - container.offsetTop
        container.scrollTo({ top, behavior: 'smooth' })
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

// --- View switcher ---------------------------------------------------
.view-switch {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem;
  border: 1px solid #ebe9f1;
  border-radius: 999px;
  background: #f8f8fc;
}

.view-switch__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.32rem 0.75rem;
  color: #6e6b7b;
  border: 0;
  border-radius: 999px;
  background: transparent;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

  &:hover:not(&--active) {
    color: #2b8a7e;
  }

  &--active {
    color: #fff;
    background: linear-gradient(135deg, #2b8a7e 0%, #255f85 100%);
    box-shadow: 0 4px 10px rgba(43, 138, 126, 0.28);
  }
}

// --- Card view ---------------------------------------------------------
.user-cards {
  position: relative;
  min-height: 220px;
  padding: 0 1.25rem 0.5rem;
}

.user-cards__loading {
  padding-top: 0.25rem;
}

.user-cards__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 3.5rem 1rem;
  color: #b9b9c3;
}

.user-cards__body {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.user-cards__scroll {
  flex: 1 1 auto;
  min-width: 0;
  max-height: 640px;
  overflow-y: auto;
  padding-right: 0.35rem;
  scroll-behavior: smooth;
}

.user-cards__group {
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0.25rem;
  }
}

.user-cards__group-heading {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.4rem 0.6rem;
  margin-bottom: 0.75rem;
  border-radius: 8px;
  background: rgba(247, 248, 251, 0.94);
  backdrop-filter: blur(3px);

  span {
    color: #2b8a7e;
    font-size: 1rem;
    font-weight: 800;
    letter-spacing: 0.02em;
  }

  em {
    color: #b9b9c3;
    font-size: 0.72rem;
    font-style: normal;
  }
}

.user-cards__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 1rem;
}

.user-card {
  position: relative;
  padding: 1rem 1rem 0.9rem;
  border: 1px solid #eeeef4;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(24, 30, 48, 0.04);
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(43, 138, 126, 0.25);
    box-shadow: 0 14px 28px rgba(24, 30, 48, 0.1);
  }
}

.user-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0.65rem;
}

.user-card__avatar {
  font-weight: 700;
}

.user-card__menu {
  margin-top: -0.35rem;
  margin-right: -0.5rem;
}

.user-card__name {
  margin: 0 0 0.45rem;
  overflow: hidden;
  color: #1f2a44;
  font-size: 0.98rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-card__role {
  margin-bottom: 0.75rem;
  font-size: 0.7rem;
}

.user-card__meta {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0;
  margin: 0;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    overflow: hidden;
    color: #6e6b7b;
    font-size: 0.78rem;
    text-overflow: ellipsis;
    white-space: nowrap;

    svg {
      flex: 0 0 auto;
      color: #b9b9c3;
    }
  }
}

// --- A-Z rail ------------------------------------------------------------
.user-cards__rail {
  position: sticky;
  top: 0.5rem;
  flex: 0 0 30px;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  padding: 0.5rem 0.2rem;
  border-radius: 999px;
  background: #f8f8fc;
}

.user-cards__rail-letter {
  width: 20px;
  padding: 0.06rem 0;
  color: #c8c7d1;
  border: 0;
  background: transparent;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1.35;
  transition: color 0.12s ease, transform 0.12s ease;

  &:disabled {
    cursor: default;
  }

  &--active {
    color: #255f85;
    cursor: pointer;

    &:hover {
      color: #2b8a7e;
      transform: scale(1.25);
    }
  }
}

@media (max-width: 767.98px) {
  .user-cards {
    padding: 0 0.75rem 0.5rem;
  }

  .user-cards__grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 0.75rem;
  }

  .view-switch__btn span {
    display: none;
  }
}
</style>
